import React, { useState, useEffect } from 'react';
import { collection, getDocs, updateDoc, doc, query, orderBy, where, getDoc, setDoc, limit } from 'firebase/firestore';
import { db } from '../../../firebase';
import { useAuth } from '../../auth/components/AuthProvider';
import { NotificationService } from '../../../utils/notificationService';
import { UserCreationService } from '../../../utils/userCreationService';

export default function AdminValidacionClientes() {
  const { rol } = useAuth();
  const [solicitudes, setSolicitudes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filtro, setFiltro] = useState('pendiente_validacion');
  const [selectedSolicitud, setSelectedSolicitud] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [observaciones, setObservaciones] = useState('');
  const [showRevalidarModal, setShowRevalidarModal] = useState(false);
  const [revalidacionData, setRevalidacionData] = useState(null);
  const [revalidando, setRevalidando] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [nuevaPassword, setNuevaPassword] = useState('');
  const [confirmarPassword, setConfirmarPassword] = useState('');
  const [solicitudParaPassword, setSolicitudParaPassword] = useState(null);
  const [estadisticas, setEstadisticas] = useState({
    total: 0,
    pendientes: 0,
    activos: 0,
    rechazados: 0,
    suspendidos: 0
  });

  useEffect(() => {
    if (rol === 'admin') {
      loadSolicitudes();
      loadEstadisticas();
    }
  }, [rol, filtro]);

  const loadSolicitudes = async () => {
    try {
      setLoading(true);
      
      let solicitudesQuery;
      if (filtro === 'todas') {
        solicitudesQuery = query(
          collection(db, 'perfiles_clientes'),
          orderBy('fechaRegistro', 'desc')
        );
      } else {
        solicitudesQuery = query(
          collection(db, 'perfiles_clientes'),
          where('estado', '==', filtro),
          orderBy('fechaRegistro', 'desc')
        );
      }
      
      const solicitudesSnapshot = await getDocs(solicitudesQuery);
      const solicitudesData = solicitudesSnapshot.docs.map(doc => ({ 
        id: doc.id, 
        ...doc.data() 
      }));
      setSolicitudes(solicitudesData);
      
    } catch (error) {
      console.error('Error loading solicitudes:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadEstadisticas = async () => {
    try {
      const todasQuery = query(collection(db, 'perfiles_clientes'));
      const todasSnapshot = await getDocs(todasQuery);
      const todas = todasSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      
      const stats = {
        total: todas.length,
        pendientes: todas.filter(p => p.estado === 'pendiente_validacion').length,
        activos: todas.filter(p => p.estado === 'activo').length,
        rechazados: todas.filter(p => p.estado === 'rechazado').length,
        suspendidos: todas.filter(p => p.estado === 'suspendido').length
      };
      
      setEstadisticas(stats);
    } catch (error) {
      console.error('Error loading statistics:', error);
    }
  };

  const validarCliente = async (solicitudId, estado, obs = '') => {
    try {
      const solicitud = solicitudes.find(s => s.id === solicitudId);

      // Buscar datos completos en solicitudes_cliente para tener uid y email
      const solicitudDoc = await getDoc(doc(db, 'solicitudes_cliente', solicitudId));
      const solicitudData = solicitudDoc.exists() ? solicitudDoc.data() : {};

      const uid = solicitudData.uid || solicitud?.userId || solicitudId;
      const email = solicitudData.email_usuario || solicitud?.email || '';
      const nombres = solicitudData.nombres || solicitud?.nombres || '';
      const apellidos = solicitudData.apellidos || solicitud?.apellidos || '';
      const telefono = solicitudData.telefono || solicitud?.telefono || '';

      // setDoc con merge crea el documento si no existe
      await setDoc(doc(db, 'perfiles_clientes', solicitudId), {
        estado,
        uid,
        userId: uid,
        email,
        nombres,
        apellidos,
        telefono,
        validadoPor: 'admin',
        fechaValidacion: new Date(),
        observaciones: obs,
        clienteId: solicitudId,
      }, { merge: true });

      await NotificationService.notifyClientValidationStatus(uid, estado, obs);
      await loadSolicitudes();
      await loadEstadisticas();
      setShowModal(false);
      setObservaciones('');
      setSelectedSolicitud(null);

    } catch (error) {
      console.error('Error validando cliente:', error);
      alert('Error al validar cliente: ' + error.message);
    }
  };

  const handleValidacion = (solicitud, estado) => {
    setSelectedSolicitud(solicitud);
    if (estado === 'rechazado') {
      setShowModal(true);
    } else {
      validarCliente(solicitud.id, estado);
    }
  };

  const getEstadoBadge = (estado) => {
    const badges = {
      'pendiente_validacion': 'bg-yellow-100 text-yellow-800',
      'activo': 'bg-green-100 text-green-800',
      'rechazado': 'bg-red-100 text-red-800',
      'suspendido': 'bg-gray-100 text-gray-800'
    };
    
    const labels = {
      'pendiente_validacion': 'Pendiente',
      'activo': 'Activo',
      'rechazado': 'Rechazado',
      'suspendido': 'Suspendido'
    };
    
    return (
      <span className={`px-2 py-1 rounded text-xs font-medium ${badges[estado]}`}>
        {labels[estado]}
      </span>
    );
  };

  const calcularDiasEspera = (fechaRegistro) => {
    const fecha = new Date(fechaRegistro.seconds * 1000);
    const hoy = new Date();
    const diferencia = hoy - fecha;
    return Math.floor(diferencia / (1000 * 60 * 60 * 24));
  };

  const revalidarLogin = async (cliente) => {
    setRevalidando(true);
    setShowRevalidarModal(true);
    setRevalidacionData(null);

    try {
      const email = cliente.email || cliente.userId;
      const uid = cliente.userId || cliente.uid;
      
      // 1. Verificar si existe en Firebase Auth
      let existeEnAuth = false;
      let metodosAuth = [];
      try {
        const authCheck = await UserCreationService.checkUserExistsInAuth(email);
        existeEnAuth = authCheck.exists;
        metodosAuth = authCheck.methods || [];
      } catch (authError) {
        console.error('Error verificando Firebase Auth:', authError);
      }

      // 2. Verificar si existe en colección usuarios
      let existeEnUsuarios = false;
      let datosUsuario = null;
      try {
        if (uid) {
          const usuarioDoc = await getDoc(doc(db, 'usuarios', uid));
          if (usuarioDoc.exists()) {
            existeEnUsuarios = true;
            datosUsuario = usuarioDoc.data();
          }
        }
        
        // Si no se encontró por UID, buscar por email
        if (!existeEnUsuarios && email) {
          const usuariosQuery = query(
            collection(db, 'usuarios'),
            where('email', '==', email)
          );
          const usuariosSnapshot = await getDocs(usuariosQuery);
          if (!usuariosSnapshot.empty) {
            existeEnUsuarios = true;
            datosUsuario = usuariosSnapshot.docs[0].data();
          }
        }
      } catch (usuariosError) {
        console.error('Error verificando colección usuarios:', usuariosError);
      }

      // 3. Buscar contraseña temporal en solicitudes_cliente
      let tienePasswordTemporal = false;
      let passwordTemporal = null;
      let solicitudId = null;
      try {
        if (email) {
          const solicitudesQuery = query(
            collection(db, 'solicitudes_cliente'),
            where('email', '==', email),
            where('usuario_creado', '==', true)
          );
          const solicitudesSnapshot = await getDocs(solicitudesQuery);
          
          if (!solicitudesSnapshot.empty) {
            const solicitudData = solicitudesSnapshot.docs[0].data();
            solicitudId = solicitudesSnapshot.docs[0].id;
            if (solicitudData.password_temporal_generado && solicitudData.password_temporal) {
              tienePasswordTemporal = true;
              passwordTemporal = solicitudData.password_temporal;
            }
          }
        }
      } catch (solicitudError) {
        console.error('Error buscando solicitud:', solicitudError);
      }

      // 4. Preparar datos de revalidación
      setRevalidacionData({
        cliente,
        email,
        uid,
        existeEnAuth,
        metodosAuth,
        existeEnUsuarios,
        datosUsuario,
        tienePasswordTemporal,
        passwordTemporal,
        solicitudId,
        puedeHacerLogin: existeEnAuth && existeEnUsuarios
      });

    } catch (error) {
      console.error('Error en revalidación:', error);
      alert('Error al revalidar el login. Por favor, intenta nuevamente.');
    } finally {
      setRevalidando(false);
    }
  };

  const guardarPasswordYRecrear = async () => {
    if (!nuevaPassword || nuevaPassword.length < 6) {
      alert('❌ La contraseña debe tener al menos 6 caracteres');
      return;
    }

    if (nuevaPassword !== confirmarPassword) {
      alert('❌ Las contraseñas no coinciden');
      return;
    }

    if (!solicitudParaPassword) {
      alert('❌ Error: No se encontró la solicitud');
      return;
    }

    try {
      setRevalidando(true);

      // Codificar la contraseña en base64
      const passwordHash = btoa(nuevaPassword);

      // Actualizar la solicitud con el password_hash
      if (solicitudParaPassword.solicitudId || revalidacionData?.solicitudId) {
        const solicitudId = solicitudParaPassword.solicitudId || revalidacionData?.solicitudId;
        await updateDoc(doc(db, 'solicitudes_cliente', solicitudId), {
          password_hash: passwordHash,
          password_actualizado_por_admin: true,
          fecha_actualizacion_password: new Date()
        });
      }

      // Actualizar solicitudData con el nuevo password_hash
      const solicitudDataActualizada = {
        ...solicitudParaPassword,
        password_hash: passwordHash
      };

      // Intentar crear el usuario nuevamente
      const resultado = await UserCreationService.createClientUser(solicitudDataActualizada);

      if (resultado.success) {
        // Cerrar modal y actualizar datos
        setShowPasswordModal(false);
        setNuevaPassword('');
        setConfirmarPassword('');
        setSolicitudParaPassword(null);

        // Actualizar datos de revalidación
        setRevalidacionData(prev => ({
          ...prev,
          existeEnAuth: true,
          puedeHacerLogin: true,
          uid: resultado.uid,
          passwordTemporal: resultado.temporaryPassword || nuevaPassword,
          tienePasswordTemporal: resultado.isTemporaryPassword
        }));

        alert('✅ Contraseña guardada y usuario recreado exitosamente en Firebase Auth.');
      } else {
        alert(`❌ Error al recrear usuario: ${resultado.error}`);
      }
    } catch (error) {
      console.error('Error guardando contraseña y recreando usuario:', error);
      alert(`❌ Error: ${error.message}`);
    } finally {
      setRevalidando(false);
    }
  };

  const recrearUsuarioEnAuth = async () => {
    if (!revalidacionData) return;

    try {
      setRevalidando(true);
      
      // Buscar la solicitud original para obtener datos
      let solicitudData = null;
      if (revalidacionData.solicitudId) {
        const solicitudDoc = await getDoc(doc(db, 'solicitudes_cliente', revalidacionData.solicitudId));
        if (solicitudDoc.exists()) {
          const solicitudRaw = solicitudDoc.data();
          // Convertir Timestamps de Firestore a Date o null
          solicitudData = { 
            id: solicitudDoc.id,
            email: solicitudRaw.email || '',
            nombres: solicitudRaw.nombres || solicitudRaw.nombre || '',
            apellidos: solicitudRaw.apellidos || '',
            telefono: solicitudRaw.telefono || '',
            direccion: solicitudRaw.direccion || '',
            comuna: solicitudRaw.comuna || '',
            region: solicitudRaw.region || '',
            rut: solicitudRaw.rut || '',
            genero: solicitudRaw.genero || '',
            password_hash: solicitudRaw.password_hash || null,
            // Convertir fecha_nacimiento si existe
            fecha_nacimiento: solicitudRaw.fecha_nacimiento ? (() => {
              try {
                if (solicitudRaw.fecha_nacimiento.toDate && typeof solicitudRaw.fecha_nacimiento.toDate === 'function') {
                  const fecha = solicitudRaw.fecha_nacimiento.toDate();
                  return isNaN(fecha.getTime()) ? null : fecha;
                } else if (solicitudRaw.fecha_nacimiento instanceof Date) {
                  return isNaN(solicitudRaw.fecha_nacimiento.getTime()) ? null : solicitudRaw.fecha_nacimiento;
                } else {
                  const fecha = new Date(solicitudRaw.fecha_nacimiento);
                  return isNaN(fecha.getTime()) ? null : fecha;
                }
              } catch (error) {
                console.warn('⚠️ Error parseando fecha_nacimiento de solicitud:', error);
                return null;
              }
            })() : null
          };
        }
      }

      // Si no hay solicitud, usar datos del perfil del cliente
      if (!solicitudData) {
        // Convertir fecha_nacimiento si es Timestamp de Firestore
        let fechaNacimiento = null;
        if (revalidacionData.cliente.fecha_nacimiento) {
          try {
            if (revalidacionData.cliente.fecha_nacimiento.toDate) {
              fechaNacimiento = revalidacionData.cliente.fecha_nacimiento.toDate();
            } else if (revalidacionData.cliente.fecha_nacimiento instanceof Date) {
              fechaNacimiento = revalidacionData.cliente.fecha_nacimiento;
            } else {
              fechaNacimiento = new Date(revalidacionData.cliente.fecha_nacimiento);
            }
            // Validar que la fecha sea válida
            if (isNaN(fechaNacimiento.getTime())) {
              fechaNacimiento = null;
            }
          } catch (error) {
            console.warn('⚠️ Error parseando fecha_nacimiento:', error);
            fechaNacimiento = null;
          }
        }
        
        solicitudData = {
          id: revalidacionData.cliente.id,
          email: revalidacionData.email,
          nombres: revalidacionData.cliente.nombres || '',
          apellidos: revalidacionData.cliente.apellidos || '',
          telefono: revalidacionData.cliente.telefono || '',
          direccion: revalidacionData.cliente.direccion || '',
          comuna: revalidacionData.cliente.comuna || '',
          region: revalidacionData.cliente.region || '',
          rut: revalidacionData.cliente.rut || '',
          fecha_nacimiento: fechaNacimiento,
          genero: revalidacionData.cliente.genero || ''
        };
      }

      // Crear usuario usando el servicio
      const resultado = await UserCreationService.createClientUser(solicitudData);

      if (resultado.success) {
        // Actualizar datos de revalidación
        setRevalidacionData(prev => ({
          ...prev,
          existeEnAuth: true,
          puedeHacerLogin: true,
          uid: resultado.uid,
          passwordTemporal: resultado.temporaryPassword || prev.passwordTemporal,
          tienePasswordTemporal: resultado.isTemporaryPassword || prev.tienePasswordTemporal
        }));

        const mensaje = resultado.usuarioYaExistia 
          ? '✅ El usuario ya existía en Firebase Auth. Se actualizó la información. El cliente ahora puede hacer login.'
          : '✅ Usuario recreado exitosamente en Firebase Auth. El cliente ahora puede hacer login.';
        
        alert(mensaje);
      } else {
        // Si el error es que no hay password_hash, mostrar modal para ingresar contraseña
        if (resultado.error && resultado.error.includes('No se encontró password_hash')) {
          setSolicitudParaPassword(solicitudData);
          setShowPasswordModal(true);
        } else if (resultado.code === 'auth/email-already-in-use' || resultado.emailExists) {
          // Si el email ya está en uso, informar y actualizar datos de revalidación
          alert(`⚠️ El email ya está registrado en Firebase Auth.\n\nEl usuario puede intentar iniciar sesión o restablecer su contraseña.\n\nSe actualizará la información de revalidación.`);
          
          // Buscar el UID del usuario existente
          try {
            const usuariosQuery = query(
              collection(db, 'usuarios'),
              where('email', '==', revalidacionData.email),
              limit(1)
            );
            const usuariosSnapshot = await getDocs(usuariosQuery);
            
            if (!usuariosSnapshot.empty) {
              const usuarioDoc = usuariosSnapshot.docs[0];
              const usuarioData = usuarioDoc.data();
              const uidExistente = usuarioData.uid || usuarioDoc.id;
              
              setRevalidacionData(prev => ({
                ...prev,
                existeEnAuth: true,
                puedeHacerLogin: true,
                uid: uidExistente
              }));
            }
          } catch (error) {
            console.error('Error buscando usuario existente:', error);
          }
        } else {
          alert(`❌ Error al recrear usuario: ${resultado.error}`);
        }
      }
    } catch (error) {
      console.error('Error recreando usuario:', error);
      alert(`❌ Error al recrear usuario: ${error.message}`);
    } finally {
      setRevalidando(false);
    }
  };

  if (rol !== 'admin') {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-red-600">Acceso Denegado</h1>
          <p className="text-gray-600">No tienes permisos para acceder a esta página.</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-300 rounded mb-6"></div>
          <div className="space-y-4">
            {[1, 2, 3, 4, 5].map(i => (
              <div key={i} className="h-20 bg-gray-300 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="bg-white rounded-lg shadow-lg p-6">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-3xl font-bold text-gray-800">Validación de Clientes</h1>
            <p className="text-gray-600">Gestiona las solicitudes de ingreso a la comunidad</p>
          </div>
        </div>

        {/* Panel de Estadísticas */}
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-8">
          <div className="bg-blue-50 p-4 rounded-lg border-l-4 border-blue-500">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-blue-600 text-sm font-medium">Total Clientes</p>
                <p className="text-2xl font-bold text-blue-800">{estadisticas.total}</p>
              </div>
              <svg className="w-8 h-8 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
            </div>
          </div>

          <div className="bg-yellow-50 p-4 rounded-lg border-l-4 border-yellow-500">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-yellow-600 text-sm font-medium">Pendientes</p>
                <p className="text-2xl font-bold text-yellow-800">{estadisticas.pendientes}</p>
              </div>
              <svg className="w-8 h-8 text-yellow-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
          </div>

          <div className="bg-green-50 p-4 rounded-lg border-l-4 border-green-500">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-green-600 text-sm font-medium">Activos</p>
                <p className="text-2xl font-bold text-green-800">{estadisticas.activos}</p>
              </div>
              <svg className="w-8 h-8 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
          </div>

          <div className="bg-red-50 p-4 rounded-lg border-l-4 border-red-500">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-red-600 text-sm font-medium">Rechazados</p>
                <p className="text-2xl font-bold text-red-800">{estadisticas.rechazados}</p>
              </div>
              <svg className="w-8 h-8 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
          </div>

          <div className="bg-gray-50 p-4 rounded-lg border-l-4 border-gray-500">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-600 text-sm font-medium">Suspendidos</p>
                <p className="text-2xl font-bold text-gray-800">{estadisticas.suspendidos}</p>
              </div>
              <svg className="w-8 h-8 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728L5.636 5.636m12.728 12.728L18.364 5.636M5.636 18.364l12.728-12.728" />
              </svg>
            </div>
          </div>
        </div>
          
        {/* Filtros */}
        <div className="flex gap-2 mb-6">
          <select
            className="border rounded px-3 py-2"
            value={filtro}
            onChange={(e) => setFiltro(e.target.value)}
          >
            <option value="pendiente_validacion">Pendientes</option>
            <option value="activo">Activos</option>
            <option value="rechazado">Rechazados</option>
            <option value="suspendido">Suspendidos</option>
            <option value="todas">Todas</option>
          </select>
        </div>

        {/* Lista de solicitudes */}
        <div className="space-y-4">
          {solicitudes.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-gray-500">No hay solicitudes con el filtro seleccionado.</p>
            </div>
          ) : (
            solicitudes.map(solicitud => (
              <div key={solicitud.id} className="border rounded-lg p-4 hover:shadow-md transition-shadow">
                <div className="flex justify-between items-start mb-3">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="font-semibold text-lg">
                        {solicitud.nombres} {solicitud.apellidos}
                      </h3>
                      {getEstadoBadge(solicitud.estado)}
                      {solicitud.creado_desde_solicitud && (
                        <span className="px-2 py-1 bg-blue-100 text-blue-700 text-xs rounded-full">
                          📝 Desde solicitud
                        </span>
                      )}
                      {solicitud.estado === 'pendiente_validacion' && (
                        <span className="text-xs text-orange-600">
                          {calcularDiasEspera(solicitud.fechaRegistro)} días esperando
                        </span>
                      )}
                    </div>
                    
                    <div className="grid md:grid-cols-3 gap-4 text-sm">
                      <div>
                        <span className="text-gray-600">RUT:</span>
                        <p className="font-medium">{solicitud.rut}</p>
                      </div>
                      <div>
                        <span className="text-gray-600">Email:</span>
                        <p className="font-medium">{solicitud.email}</p>
                      </div>
                      <div>
                        <span className="text-gray-600">Teléfono:</span>
                        <p className="font-medium">{solicitud.telefono}</p>
                      </div>
                      <div>
                        <span className="text-gray-600">Ubicación:</span>
                        <p className="font-medium">{solicitud.comuna}, {solicitud.region}</p>
                      </div>
                      <div>
                        <span className="text-gray-600">Tipo:</span>
                        <p className="font-medium">{solicitud.tipoCliente}</p>
                      </div>
                      <div>
                        <span className="text-gray-600">Fecha registro:</span>
                        <p className="font-medium">
                          {new Date(solicitud.fechaRegistro.seconds * 1000).toLocaleDateString()}
                        </p>
                      </div>
                    </div>
                    
                    {solicitud.motivoRegistro && (
                      <div className="mt-3 bg-gray-50 p-3 rounded">
                        <span className="text-sm font-medium text-gray-700">Motivo de registro:</span>
                        <p className="text-sm text-gray-600 mt-1">{solicitud.motivoRegistro}</p>
                      </div>
                    )}
                    
                    {solicitud.creado_desde_solicitud && solicitud.solicitud_original_id && (
                      <div className="mt-3 bg-blue-50 p-3 rounded">
                        <span className="text-sm font-medium text-blue-700">Cliente creado desde solicitud:</span>
                        <a 
                          href={`/admin/solicitudes-cliente?highlight=${solicitud.solicitud_original_id}`}
                          className="text-sm text-blue-600 hover:text-blue-800 underline ml-2"
                          target="_blank"
                        >
                          Ver solicitud original →
                        </a>
                      </div>
                    )}
                    
                    {solicitud.observaciones && (
                      <div className="mt-3 bg-red-50 p-3 rounded">
                        <span className="text-sm font-medium text-red-700">Observaciones:</span>
                        <p className="text-sm text-red-600 mt-1">{solicitud.observaciones}</p>
                      </div>
                    )}
                  </div>
                </div>
                
                {/* Acciones */}
                {solicitud.estado === 'pendiente_validacion' && (
                  <div className="flex gap-2 pt-3 border-t">
                    <button
                      className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700"
                      onClick={() => handleValidacion(solicitud, 'activo')}
                    >
                      ✅ Aprobar
                    </button>
                    <button
                      className="bg-red-600 text-white px-4 py-2 rounded hover:bg-red-700"
                      onClick={() => handleValidacion(solicitud, 'rechazado')}
                    >
                      ❌ Rechazar
                    </button>
                    <button className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700">
                      📧 Contactar
                    </button>
                    <button className="bg-gray-600 text-white px-4 py-2 rounded hover:bg-gray-700">
                      📋 Ver Historial
                    </button>
                  </div>
                )}
                
                {(solicitud.estado === 'activo' || solicitud.estado === 'pendiente_validacion') && (
                  <div className="flex gap-2 pt-3 border-t">
                    <button
                      className="bg-purple-600 text-white px-4 py-2 rounded hover:bg-purple-700"
                      onClick={() => revalidarLogin(solicitud)}
                      title="Verificar estado de autenticación del usuario"
                    >
                      🔐 Revalidar Login
                    </button>
                    {solicitud.estado === 'activo' && (
                      <>
                        <button
                          className="bg-gray-600 text-white px-4 py-2 rounded hover:bg-gray-700"
                          onClick={() => handleValidacion(solicitud, 'suspendido')}
                        >
                          ⏸️ Suspender
                        </button>
                        <button className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700">
                          👁️ Ver Actividad
                        </button>
                      </>
                    )}
                  </div>
                )}
                
                {solicitud.estado === 'suspendido' && (
                  <div className="flex gap-2 pt-3 border-t">
                    <button
                      className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700"
                      onClick={() => handleValidacion(solicitud, 'activo')}
                    >
                      ▶️ Reactivar
                    </button>
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      </div>

      {/* Modal para ingresar contraseña cuando falta password_hash */}
      {showPasswordModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h3 className="text-xl font-semibold mb-4 text-red-600">🔑 Ingresar Contraseña</h3>
            
            <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mb-4">
              <p className="text-sm text-yellow-700">
                <strong>⚠️ No se encontró password_hash en la solicitud.</strong>
              </p>
              <p className="text-sm text-yellow-700 mt-2">
                Por favor, ingresa una contraseña para el usuario. Esta contraseña se guardará en la solicitud y se usará para crear/recrear el usuario en Firebase Auth.
              </p>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Nueva Contraseña
                </label>
                <input
                  type="password"
                  value={nuevaPassword}
                  onChange={(e) => setNuevaPassword(e.target.value)}
                  className="w-full border rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-purple-500"
                  placeholder="Mínimo 6 caracteres"
                  disabled={revalidando}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Confirmar Contraseña
                </label>
                <input
                  type="password"
                  value={confirmarPassword}
                  onChange={(e) => setConfirmarPassword(e.target.value)}
                  className="w-full border rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-purple-500"
                  placeholder="Repite la contraseña"
                  disabled={revalidando}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter' && nuevaPassword && confirmarPassword) {
                      guardarPasswordYRecrear();
                    }
                  }}
                />
              </div>
            </div>

            <div className="flex gap-2 mt-6">
              <button
                onClick={guardarPasswordYRecrear}
                disabled={revalidando || !nuevaPassword || !confirmarPassword}
                className="flex-1 bg-purple-600 text-white py-2 px-4 rounded hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {revalidando ? '⏳ Guardando y Recreando...' : '✅ Guardar y Recrear Usuario'}
              </button>
              <button
                onClick={() => {
                  setShowPasswordModal(false);
                  setNuevaPassword('');
                  setConfirmarPassword('');
                  setSolicitudParaPassword(null);
                }}
                disabled={revalidando}
                className="flex-1 border border-gray-300 text-gray-700 py-2 px-4 rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Cancelar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Revalidación de Login */}
      {showRevalidarModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <h3 className="text-xl font-semibold mb-4">🔐 Revalidación de Login</h3>
            
            {revalidando && !revalidacionData ? (
              <div className="text-center py-8">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto mb-4"></div>
                <p className="text-gray-600">Verificando estado de autenticación...</p>
              </div>
            ) : revalidacionData ? (
              <div className="space-y-4">
                {/* Información del Cliente */}
                <div className="bg-gray-50 p-4 rounded">
                  <h4 className="font-semibold mb-2">Información del Cliente</h4>
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <div><span className="text-gray-600">Email:</span> <span className="font-medium">{revalidacionData.email}</span></div>
                    <div><span className="text-gray-600">UID:</span> <span className="font-medium font-mono text-xs">{revalidacionData.uid || 'No disponible'}</span></div>
                  </div>
                </div>

                {/* Estado de Firebase Auth */}
                <div className={`p-4 rounded border-l-4 ${revalidacionData.existeEnAuth ? 'bg-green-50 border-green-500' : 'bg-red-50 border-red-500'}`}>
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="font-semibold">Firebase Authentication</h4>
                    <span className={`px-3 py-1 rounded text-sm font-medium ${revalidacionData.existeEnAuth ? 'bg-green-200 text-green-800' : 'bg-red-200 text-red-800'}`}>
                      {revalidacionData.existeEnAuth ? '✅ Existe' : '❌ No existe'}
                    </span>
                  </div>
                  {revalidacionData.metodosAuth && revalidacionData.metodosAuth.length > 0 && (
                    <p className="text-sm text-gray-600">Métodos: {revalidacionData.metodosAuth.join(', ')}</p>
                  )}
                </div>

                {/* Estado de Colección Usuarios */}
                <div className={`p-4 rounded border-l-4 ${revalidacionData.existeEnUsuarios ? 'bg-green-50 border-green-500' : 'bg-red-50 border-red-500'}`}>
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="font-semibold">Colección Usuarios (Firestore)</h4>
                    <span className={`px-3 py-1 rounded text-sm font-medium ${revalidacionData.existeEnUsuarios ? 'bg-green-200 text-green-800' : 'bg-red-200 text-red-800'}`}>
                      {revalidacionData.existeEnUsuarios ? '✅ Existe' : '❌ No existe'}
                    </span>
                  </div>
                  {revalidacionData.datosUsuario && (
                    <div className="text-sm text-gray-600 mt-2">
                      <p>Rol: <span className="font-medium">{revalidacionData.datosUsuario.rol || 'N/A'}</span></p>
                      <p>Activo: <span className="font-medium">{revalidacionData.datosUsuario.activo ? 'Sí' : 'No'}</span></p>
                    </div>
                  )}
                </div>

                {/* Estado General */}
                <div className={`p-4 rounded border-l-4 ${revalidacionData.puedeHacerLogin ? 'bg-green-50 border-green-500' : 'bg-yellow-50 border-yellow-500'}`}>
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="font-semibold">Estado General</h4>
                    <span className={`px-3 py-1 rounded text-sm font-medium ${revalidacionData.puedeHacerLogin ? 'bg-green-200 text-green-800' : 'bg-yellow-200 text-yellow-800'}`}>
                      {revalidacionData.puedeHacerLogin ? '✅ Puede hacer login' : '⚠️ No puede hacer login'}
                    </span>
                  </div>
                  {!revalidacionData.puedeHacerLogin && (
                    <p className="text-sm text-gray-600 mt-2">
                      El usuario no puede hacer login porque {!revalidacionData.existeEnAuth ? 'no existe en Firebase Auth' : 'no existe en la colección usuarios'}.
                    </p>
                  )}
                </div>

                {/* Contraseña Temporal */}
                {revalidacionData.tienePasswordTemporal && revalidacionData.passwordTemporal && (
                  <div className="bg-blue-50 p-4 rounded border-l-4 border-blue-500">
                    <h4 className="font-semibold mb-2">🔑 Contraseña Temporal</h4>
                    <div className="bg-white border-2 border-blue-300 rounded p-3 mb-2">
                      <p className="text-lg font-mono font-bold text-blue-900 text-center">{revalidacionData.passwordTemporal}</p>
                    </div>
                    <p className="text-sm text-blue-700">
                      Esta contraseña fue generada automáticamente. El cliente debe usarla para iniciar sesión.
                    </p>
                  </div>
                )}

                {/* Acciones */}
                <div className="flex gap-2 pt-4 border-t">
                  {!revalidacionData.existeEnAuth && (
                    <button
                      className="flex-1 bg-purple-600 text-white py-2 px-4 rounded hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed"
                      onClick={recrearUsuarioEnAuth}
                      disabled={revalidando}
                    >
                      {revalidando ? '⏳ Recreando...' : '🔄 Recrear Usuario en Firebase Auth'}
                    </button>
                  )}
                  <button
                    className="flex-1 border border-gray-300 text-gray-700 py-2 px-4 rounded hover:bg-gray-50"
                    onClick={() => {
                      setShowRevalidarModal(false);
                      setRevalidacionData(null);
                    }}
                  >
                    Cerrar
                  </button>
                </div>
              </div>
            ) : (
              <div className="text-center py-8">
                <p className="text-gray-600">No se pudo obtener información de revalidación.</p>
                <button
                  className="mt-4 border border-gray-300 text-gray-700 py-2 px-4 rounded hover:bg-gray-50"
                  onClick={() => setShowRevalidarModal(false)}
                >
                  Cerrar
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Modal para observaciones de rechazo */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold mb-4">Motivo del Rechazo</h3>
            
            <div className="mb-4">
              <label className="block text-sm font-medium mb-2">
                Explica el motivo del rechazo:
              </label>
              <textarea
                rows="4"
                className="w-full border rounded px-3 py-2"
                placeholder="Especifica las razones por las cuales no se puede aprobar esta solicitud..."
                value={observaciones}
                onChange={(e) => setObservaciones(e.target.value)}
                required
              />
            </div>
            
            <div className="flex gap-2">
              <button
                className="flex-1 border border-gray-300 text-gray-700 py-2 px-4 rounded hover:bg-gray-50"
                onClick={() => {
                  setShowModal(false);
                  setObservaciones('');
                  setSelectedSolicitud(null);
                }}
              >
                Cancelar
              </button>
              <button
                className="flex-1 bg-red-600 text-white py-2 px-4 rounded hover:bg-red-700"
                onClick={() => {
                  if (observaciones.trim()) {
                    validarCliente(selectedSolicitud.id, 'rechazado', observaciones);
                  } else {
                    alert('Debes especificar el motivo del rechazo');
                  }
                }}
              >
                Confirmar Rechazo
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
